import {Component, computed, effect, inject, input, OnInit, output, signal, ChangeDetectionStrategy} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {MatTooltipModule} from "@angular/material/tooltip"
import {OwnOrganizationsFilterSiloOrgGroupGQL} from "@app/common/components/filters/own-organizations-filter/own-organizations-filter.generated"
import {fetchIgnoringErrors} from "@common/helpers/api/fetch"
import {FilterListComponent} from "@common/components/filters/filter-list/filter-list.component"
import {PlaceholderComponent} from "@common/components/placeholders/placeholder/placeholder.component"
import {AuthService} from "@common/services/auth/auth.service"
import {FiltersService} from "@common/services/filters/filters.service"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
type SelectedOrganization = {state: string; label: string | undefined}

const PUBLIC_LIBRARY_KEY = "publicLibrary"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-own-organizations-filter",
    imports: [FilterListComponent, MatTooltipModule, PlaceholderComponent],
    templateUrl: "./own-organizations-filter.component.html",
    styleUrl: "./own-organizations-filter.component.scss",
})
export class OwnOrganizationsFilterComponent implements OnInit {
    readonly permission = inject(PermissionsService)
    readonly organizations = inject(OrganizationsService)
    private readonly auth = inject(AuthService)
    private readonly filtersService = inject(FiltersService)
    private readonly ownOrganizationsFilterSiloOrgGroup = inject(OwnOrganizationsFilterSiloOrgGroupGQL)
    $can = this.permission.$to

    readonly $initialSelection = input<"url" | "ownOrganization">("ownOrganization", {alias: "initialSelection"})
    readonly $locked = input(false, {alias: "locked"})
    // The boolean filter criterion the "Public Library" chip drives (e.g. "publicDownloads" for materials,
    // "public" for models/templates). Null hides the chip.
    readonly $publicLibraryCriterion = input<string | null>(null, {alias: "publicLibraryCriterion"})
    readonly selectionChanged = output<string[] | undefined>()

    readonly $selectedOrganizations = signal<SelectedOrganization[]>([])

    // --- Organization group logic (absorbed from OrganizationGroupFilterComponent) ---

    private readonly $selectedIds = signal<Set<string>>(new Set())

    // Organization group data for the current silo organization (loaded for staff users)
    readonly $siloOrgGroup = signal<{id: string; organizations: {id: string; name?: string | null; isActive: boolean}[]} | null>(null)
    private siloOrgGroupLoadedForOrgId: string | null = null

    // Load silo org group reactively when staff status or current org changes
    private readonly siloOrgGroupEffect = effect(() => {
        const isStaff = this.auth.$isStaff()
        const currentOrg = this.organizations.$current()
        if (isStaff && currentOrg && this.siloOrgGroupLoadedForOrgId !== currentOrg.id) {
            this.siloOrgGroupLoadedForOrgId = currentOrg.id
            this.$siloOrgGroup.set(null)
            void this.loadSiloOrgGroup(currentOrg.id)
        }
    })

    // All orgs in the organization group (stable order: current org first, then siblings)
    private readonly $groupOrganizations = computed(() => {
        const currentOrg = this.organizations.$current()

        // Get all orgs in the group: for staff use silo org group, for non-staff use membership data
        // Filter out inactive organizations so they don't appear in filter shortcuts
        const allGroupOrgs = (
            this.auth.$isStaff()
                ? (this.$siloOrgGroup()?.organizations ?? [])
                : (this.auth.$user()?.memberships ?? []).flatMap((m) => m.organization.organizationGroup?.organizations ?? [])
        ).filter((org) => org.isActive !== false)

        if (allGroupOrgs.length <= 1) return []

        const groupOrganizations: {id: string; label: string}[] = []
        const seen = new Set<string>()

        if (currentOrg) {
            const inGroup = allGroupOrgs.some((org) => org.id === currentOrg.id)
            if (inGroup && currentOrg.name) {
                seen.add(currentOrg.id)
                groupOrganizations.push({id: currentOrg.id, label: currentOrg.name})
            }
        }

        for (const org of allGroupOrgs) {
            if (!seen.has(org.id) && org.name) {
                seen.add(org.id)
                groupOrganizations.push({id: org.id, label: org.name})
            }
        }

        return groupOrganizations
    })

    // Toggle buttons for all orgs in the organization group (with selection state)
    public readonly $organizationShortcuts = computed(() => {
        const selectedIds = this.$selectedIds()
        return this.$groupOrganizations().map((org) => ({...org, selected: selectedIds.has(org.id)}))
    })

    public readonly $hasOrgGroupShortcuts = computed(() => this.$organizationShortcuts().length > 0)

    // --- Radio library filter (non-staff users on URL-filter-driven pages) ---

    // Single-select chips: one per group organization (or "My Library" for single-org users),
    // plus "Public Library" (publicly downloadable materials) where enabled
    public readonly $isRadioMode = computed(
        () => this.$initialSelection() === "url" && !this.$locked() && this.auth.$user() !== undefined && !this.auth.$actingAsStaff(),
    )

    public readonly $libraryChips = computed(() => {
        if (!this.$isRadioMode()) return []
        const chips: {key: string; label: string}[] = []
        const groupOrganizations = this.$groupOrganizations()
        if (groupOrganizations.length) {
            chips.push(...groupOrganizations.map((org) => ({key: org.id, label: org.label})))
        } else {
            const currentOrg = this.organizations.$current()
            if (currentOrg) {
                chips.push({key: currentOrg.id, label: "My Library"})
            }
        }
        if (this.$publicLibraryCriterion()) {
            chips.push({key: PUBLIC_LIBRARY_KEY, label: "Public Library"})
        }
        return chips
    })

    public readonly $selectedLibraryChipKey = signal<string | null>(null)

    private readonly syncRadioSelectionFromUrl = this.filtersService.states.pipe(takeUntilDestroyed()).subscribe((states) => {
        const criterion = this.$publicLibraryCriterion()
        const organizationIds = Array.from(states.criteria["organizationId"]?.values() ?? [])
        const publicLibrarySelected = criterion ? (states.criteria[criterion]?.has("1") ?? false) : false
        this.$selectedLibraryChipKey.set(publicLibrarySelected ? PUBLIC_LIBRARY_KEY : (organizationIds[0] ?? null))
    })

    async onToggleLibraryChip(chip: {key: string}) {
        const criterion = this.$publicLibraryCriterion()
        const nextKey = this.$selectedLibraryChipKey() === chip.key ? null : chip.key
        this.$selectedLibraryChipKey.set(nextKey)
        const criteria: Record<string, Iterable<string>> = {
            organizationId: nextKey && nextKey !== PUBLIC_LIBRARY_KEY ? [nextKey] : [],
        }
        if (criterion) {
            criteria[criterion] = nextKey === PUBLIC_LIBRARY_KEY ? ["1"] : []
        }
        await this.filtersService.updateMultipleCriteria(criteria)
    }

    // Select the user's own library by default when landing on a page without an organization filter
    private defaultLibrarySelectionApplied = false
    private readonly defaultLibrarySelectionEffect = effect(() => {
        if (this.defaultLibrarySelectionApplied) return
        if (this.$initialSelection() !== "url" || this.$locked()) return
        const user = this.auth.$user()
        if (user === undefined) return
        if (!user || this.auth.$actingAsStaff()) {
            this.defaultLibrarySelectionApplied = true
            return
        }
        const currentOrg = this.organizations.$current()
        if (!currentOrg) return
        this.defaultLibrarySelectionApplied = true
        const {criteria} = this.filtersService.currentStates
        const criterion = this.$publicLibraryCriterion()
        if (criteria["organizationId"]?.size || (criterion && criteria[criterion]?.size)) return
        this.$selectedLibraryChipKey.set(currentOrg.id)
        void this.filtersService.updateMultipleCriteria({organizationId: [currentOrg.id]})
    })

    private async loadSiloOrgGroup(orgId: string) {
        const {organization} = await fetchIgnoringErrors(this.ownOrganizationsFilterSiloOrgGroup)({id: orgId}, {context: {withoutSilo: true}})
        this.$siloOrgGroup.set(organization?.organizationGroup ?? null)
    }

    onToggleOrganizationShortcut(shortcut: {id: string; selected: boolean}) {
        const currentIds = this.$selectedIds()
        const newIds = new Set(currentIds)
        if (shortcut.selected) {
            newIds.delete(shortcut.id)
        } else {
            newIds.add(shortcut.id)
        }
        this.$selectedIds.set(newIds)
        this.selectionChanged.emit(newIds.size > 0 ? [...newIds] : undefined)
    }

    // --- Own organizations logic ---

    // Update selectedOrganization labels once org data becomes available
    private readonly resolveLabelsEffect = effect(() => {
        const all = this.organizations.$all()
        if (all && this.$selectedOrganizations().some((o) => o.label === undefined)) {
            this.$selectedOrganizations.update((selectedOrganizations) =>
                selectedOrganizations.map((o) => {
                    if (o.label === undefined) {
                        const org = all.find((a) => a.id === o.state)
                        return {...o, label: org?.name ?? o.state}
                    }
                    return o
                }),
            )
        }
    })

    ngOnInit() {
        if (this.$locked()) {
            const currentOrg = this.organizations.$current()
            if (currentOrg) {
                this.$selectedOrganizations.set([{state: currentOrg.id, label: currentOrg.name ?? undefined}])
                this.$selectedIds.set(new Set([currentOrg.id]))
                this.selectionChanged.emit([currentOrg.id])
            }
        } else if (this.$initialSelection() === "url") {
            const urlOrgIds = Array.from(this.filtersService.currentStates.criteria["organizationId"]?.values() ?? [])
            if (urlOrgIds.length) {
                this.$selectedIds.set(new Set(urlOrgIds))
                this.$selectedOrganizations.set(
                    urlOrgIds.map((id) => {
                        const org = this.organizations.$byId(id)()
                        return {state: id, label: org?.name ?? undefined}
                    }),
                )
                this.selectionChanged.emit(urlOrgIds)
            } else {
                this.selectionChanged.emit(undefined)
            }
        } else if (this.auth.$actingAsStaff()) {
            this.selectionChanged.emit(undefined)
        } else {
            this.selectOwnOrganizations()
        }
    }

    onRemoveOrganization(organization: SelectedOrganization) {
        this.$selectedOrganizations.set(this.$selectedOrganizations().filter((selectedOrganization) => selectedOrganization.state !== organization.state))
        this.selectionChanged.emit(this.$selectedOrganizations().length ? this.$selectedOrganizations().map((organization) => organization.state) : undefined)
    }

    selectOwnOrganizations() {
        this.$selectedOrganizations.set(
            this.organizations
                .$own()
                ?.filter((organization) => organization.isActive)
                ?.map((organization) => ({
                    label: organization.name ?? "",
                    state: organization.id,
                })) ?? [],
        )
        const ids = this.$selectedOrganizations().map((organization) => organization.state)
        this.$selectedIds.set(new Set(ids))
        this.selectionChanged.emit(ids)
    }
}
